import React, { useState, useEffect, useLayoutEffect } from 'react';
import { CONNECTION_FAILURE_MESSAGE, RETRYING_MESSAGE } from '../shared/connectionCopy';

/**
 * Toast Notification Component
 * Replaces alert() dialogs with non-blocking toast messages
 *
 * Usage:
 * import { useToast } from './Toast';
 * const { showToast } = useToast();
 * showToast('Success!', 'success');
 * showToast('Error occurred', 'error');
 */

let globalShowToast = null;
let globalClearToasts = null;

export const TOAST_LAYER_STYLE = {
  position: 'fixed',
  top: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)',
  right: 'max(1rem, env(safe-area-inset-right, 0px))',
  bottom: 'calc(env(safe-area-inset-bottom, 0px) + 96px)',
  left: 'max(1rem, env(safe-area-inset-left, 0px))',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  justifyContent: 'flex-end',
  zIndex: 99999,
  pointerEvents: 'none',
  overflow: 'hidden',
  boxSizing: 'border-box',
};

/** Signup plan cards and open modals keep the toast in the top band. */
export function toastLayerStyle(planPicker, modalOpen) {
  if (!planPicker && !modalOpen) return TOAST_LAYER_STYLE;
  return {
    ...TOAST_LAYER_STYLE,
    top: modalOpen && !planPicker
      ? 'calc(env(safe-area-inset-top, 0px) + var(--mobile-top-bar-height, 44px) + 8px)'
      : TOAST_LAYER_STYLE.top,
    bottom: 'auto',
    height: 'auto',
    alignItems: 'center',
    justifyContent: 'flex-start',
  };
}

function inferToastType(message) {
  const text = String(message || '');
  if (/\berror\b|\bfailed\b|\binvalid\b|\bunable\b|\bcould not\b/i.test(text)) return 'error';
  if (/\bsuccess|\bjoined\b|\bwelcome\b|\bsaved\b|\bupdated\b|\buploaded\b|\bcomplete\b|\bcreated\b|\bdeleted\b|\bcopied\b|\badded successfully\b/i.test(text)) return 'success';
  if (/^please\b/i.test(text.trim())) return 'error';
  return 'info';
}

const FLASH_KEY = 'pacarion.toast';

/** Survives a reload, so a save that refreshes the page can still confirm. */
export function flashToast(message, type = 'success') {
  try {
    sessionStorage.setItem(FLASH_KEY, JSON.stringify({ message, type }));
  } catch (error) {
    // Private mode can block storage. The in-page toast is the other path.
  }
}

export function consumeFlashToast() {
  try {
    const raw = sessionStorage.getItem(FLASH_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(FLASH_KEY);
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.message) return null;
    return parsed;
  } catch (error) {
    return null;
  }
}

export function showToast(message, type, duration = 4000) {
  if (!globalShowToast) return;
  globalShowToast(message, type || inferToastType(message), duration);
}

/** Drops visible toasts. A tab change should not keep a confirmation from the last screen. */
export function clearToasts() {
  if (globalClearToasts) globalClearToasts();
}

export const useToast = () => ({ showToast });

export const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  const [abovePlanCards, setAbovePlanCards] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    // Modals portal to document.body, outside #root. Watch the body so an
    // open sheet moves the toast on the same turn it appears.
    const root = document.body;
    const sync = () => {
      const next = Boolean(document.querySelector('.plan-picker'));
      const modal = Boolean(document.querySelector('.app-modal-layer'));
      setAbovePlanCards((current) => (current === next ? current : next));
      setModalOpen((current) => (current === modal ? current : modal));
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const planPickerOpen = abovePlanCards || Boolean(typeof document !== 'undefined' && document.querySelector('.plan-picker'));
  const modalLayerOpen = modalOpen || Boolean(typeof document !== 'undefined' && document.querySelector('.app-modal-layer'));

  useLayoutEffect(() => {
    const holdSpace = planPickerOpen && toasts.length > 0;
    document.documentElement.classList.toggle('plan-picker-toast', holdSpace);
    return () => document.documentElement.classList.remove('plan-picker-toast');
  }, [planPickerOpen, toasts.length]);

  useEffect(() => {
    globalShowToast = (message, type, duration) => {
      const id = Date.now() + Math.random();
      setToasts((prev) => {
        const kept = message === CONNECTION_FAILURE_MESSAGE
          ? prev.filter((toast) => toast.message !== RETRYING_MESSAGE)
          : [];
        if (kept.some((toast) => toast.message === message)) return kept;
        return [{ id, message, type, duration }];
      });

      if (duration > 0) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((toast) => toast.id !== id));
        }, duration);
      }
    };

    globalClearToasts = () => setToasts([]);

    const flashed = consumeFlashToast();
    if (flashed) globalShowToast(flashed.message, flashed.type || 'success', 4000);

    return () => {
      globalShowToast = null;
      globalClearToasts = null;
    };
  }, []);

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const getToastStyle = (type) => {
    const baseStyle = {
      position: 'relative',
      padding: '1rem 1.25rem',
      borderRadius: '0.75rem',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.75rem',
      marginBottom: '0.75rem',
      minWidth: 0,
      width: 'min(400px, 100%)',
      maxWidth: '100%',
      overflow: 'hidden',
      pointerEvents: 'auto',
      boxSizing: 'border-box',
      animation: 'slideIn 0.3s ease-out',
      cursor: 'pointer'
    };

    const styles = {
      success: {
        ...baseStyle,
        background: '#10b981',
        color: 'white'
      },
      error: {
        ...baseStyle,
        background: '#ef4444',
        color: 'white'
      },
      warning: {
        ...baseStyle,
        background: '#f59e0b',
        color: 'white'
      },
      info: {
        ...baseStyle,
        background: '#3b82f6',
        color: 'white'
      }
    };

    return styles[type] || styles.info;
  };

  const getIcon = (type) => {
    const icons = {
      success: '✓',
      error: '✕',
      warning: '⚠',
      info: 'ℹ'
    };
    return icons[type] || icons.info;
  };

  if (toasts.length === 0) return null;

  return (
    <>
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
      <div className="app-toast" style={toastLayerStyle(planPickerOpen, modalLayerOpen)}>
        {toasts.map(toast => (
          <div
            key={toast.id}
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              removeToast(toast.id);
            }}
            style={getToastStyle(toast.type)}
          >
            <div style={{
              fontSize: '1.25rem',
              fontWeight: 'bold',
              lineHeight: 1
            }}>
              {getIcon(toast.type)}
            </div>
            <div style={{
              flex: '1 1 auto',
              minWidth: 0,
              maxWidth: '100%',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              fontSize: '0.95rem',
              lineHeight: '1.4'
            }}>
              {toast.message}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
